import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { currency } from "@/lib/format";

/**
 * Menu explorer with sticky category tabs.
 * - Groups menu items by category
 * - Allows adding items to the cart via onAdd()
 */
export default function MenuExplorer({ categories, onAdd }) {
    const cats = useMemo(() => Object.keys(categories), [categories]);
    const [active, setActive] = useState(cats[0] || "");

    useEffect(() => {
        if (!active && cats.length) setActive(cats[0]);
    }, [cats, active]);

    return (
        <div>
            <div className="sticky top-[120px] z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 py-2 bg-background/90 backdrop-blur border-b border-border">
                <div className="flex gap-1 overflow-x-auto" data-testid="menu-category-tabs">
                    {cats.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActive(c)}
                            data-testid={`menu-category-${c.toLowerCase()}`}
                            className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
                                active === c
                                    ? "bg-secondary text-secondary-foreground"
                                    : "text-muted-foreground hover:bg-accent/40"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(categories[active] || []).map((item) => (
                    <MenuItemCard key={item.id} item={item} onAdd={onAdd} />
                ))}
            </div>
        </div>
    );
}

function MenuItemCard({ item, onAdd }) {
    return (
        <Card className="overflow-hidden" data-testid="menu-item-card">
            {item.image_url && (
                <div className="aspect-[16/9] overflow-hidden">
                    <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />
                </div>
            )}
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <div className="font-medium">{item.name}</div>
                    <div className="font-semibold">{currency(item.price)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2 min-h-[2.4em]">{item.description}</div>
                <Button
                    className="w-full mt-3"
                    variant="secondary"
                    onClick={() => onAdd(item)}
                    data-testid="menu-item-add-button"
                >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add to order
                </Button>
            </CardContent>
        </Card>
    );
}
